import React from 'react';

function Home({ onGetStarted, onSignIn, onShowAbout, onShowContact }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#1a2942] to-[#0d47a1] text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Glowing Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl animate-pulse-slow animation-delay-4000"></div>
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-purple-500/15 rounded-full blur-3xl animate-pulse-slow animation-delay-3000"></div>
        
        {/* Animated Grid */}
        <div className="absolute inset-0 animate-grid-move" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.05) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Floating Particles */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/60 rounded-full animate-float shadow-lg shadow-blue-400/50"></div>
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-cyan-400/60 rounded-full animate-float animation-delay-1000 shadow-lg shadow-cyan-400/50"></div>
        <div className="absolute bottom-1/3 left-1/2 w-2 h-2 bg-indigo-400/60 rounded-full animate-float animation-delay-2000 shadow-lg shadow-indigo-400/50"></div>
        <div className="absolute top-2/3 right-1/4 w-3 h-3 bg-blue-400/60 rounded-full animate-float animation-delay-3000 shadow-lg shadow-blue-400/50"></div>
        <div className="absolute top-1/2 left-1/5 w-2 h-2 bg-purple-400/60 rounded-full animate-float animation-delay-4000 shadow-lg shadow-purple-400/50"></div>
        <div className="absolute bottom-1/4 right-1/2 w-3 h-3 bg-cyan-400/60 rounded-full animate-float animation-delay-1500 shadow-lg shadow-cyan-400/50"></div>
        
        {/* Moving Lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-blue-400/20 to-transparent animate-line-down"></div>
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-cyan-400/20 to-transparent animate-line-down animation-delay-2000"></div>
        
        {/* Circuit Pattern */}
        <div className="absolute top-1/3 left-10 w-40 h-40 opacity-20 animate-spin-slow">
          <div className="absolute top-0 left-0 w-full h-full border-2 border-blue-400/30 rounded-lg"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 border-2 border-cyan-400/30 rounded-lg"></div>
        </div>
        <div className="absolute bottom-1/4 right-20 w-32 h-32 opacity-20 animate-spin-slow animation-delay-3000">
          <div className="absolute top-0 left-0 w-full h-full border-2 border-purple-400/30 rounded-lg"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 border-2 border-indigo-400/30 rounded-lg"></div>
        </div>
        
        {/* Data Stream Effect */}
        <div className="absolute top-20 right-10 opacity-30">
          <div className="text-blue-400/40 text-xs font-mono animate-data-stream">01010101</div>
        </div>
        <div className="absolute bottom-40 left-20 opacity-30 animation-delay-2000">
          <div className="text-cyan-400/40 text-xs font-mono animate-data-stream">11001100</div>
        </div>
        <div className="absolute top-1/2 right-1/3 opacity-30 animation-delay-4000">
          <div className="text-indigo-400/40 text-xs font-mono animate-data-stream">10101010</div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex justify-between items-center px-16 py-6 relative z-10 animate-fade-in">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="TwoRun.AI Logo" className="w-12 h-12 object-contain animate-float-gentle" />
          <span className="text-2xl font-bold">TwoRun.AI</span>
        </div>
        
        <div className="hidden md:flex gap-10">
          <a href="#home" className="text-white/80 hover:text-white transition-all hover:scale-110">Home</a>
          <button onClick={onShowAbout} className="text-white/80 hover:text-white transition-all hover:scale-110">About</button>
          <button onClick={onShowContact} className="text-white/80 hover:text-white transition-all hover:scale-110">Contact</button>
        </div>
        
        <div className="flex gap-4">
          <button 
            onClick={onSignIn}
            className="px-6 py-2 border border-white/30 rounded-lg hover:border-white hover:bg-white/10 transition-all hover:scale-105"
          >
            Sign in
          </button>
          <button 
            onClick={onGetStarted}
            className="px-6 py-2 bg-blue-500 rounded-lg hover:bg-blue-600 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-105"
          >
            Get started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="text-center px-8 py-24 relative z-10">
        <div className="inline-block bg-white/10 backdrop-blur-md border border-white/20 px-6 py-2 rounded-full text-xs tracking-wider mb-8 animate-fade-in-up animation-delay-500">
          WE'VE ANNOUNCED AI+ 2.0. CHECK IT OUT
        </div>
        
        <h1 className="text-6xl md:text-7xl font-bold leading-tight mb-8 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent animate-fade-in-up animation-delay-700">
          The easiest way to power<br />
          up your business with AI
        </h1>
        
        <p className="text-xl text-white/70 leading-relaxed mb-12 max-w-3xl mx-auto animate-fade-in-up animation-delay-1000">
          Advanced AI assistant that learns from your interactions<br />
          and provides intelligent, personalized responses
        </p>
        
        <div className="flex gap-4 justify-center animate-fade-in-up animation-delay-1300">
          <button 
            onClick={onGetStarted}
            className="px-10 py-4 bg-blue-500 rounded-xl text-lg font-semibold hover:bg-blue-600 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-500/40 transition-all hover:scale-105"
          >
            Get started
          </button>
          <a 
            href="https://oawo.co.uk/en/page/ai-model-training"
            target="_blank"
            rel="noopener noreferrer"
            className="px-10 py-4 border-2 border-white/30 rounded-xl text-lg font-semibold hover:border-white hover:bg-white/10 transition-all hover:scale-105"
          >
            Learn more
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer id="contact" className="absolute bottom-0 w-full bg-[#0a1628]/50 backdrop-blur-sm px-16 py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-white/60 mb-2">
            Contact: sales@tworun.ai
          </p>
          <p className="text-xs text-white/40">
            🚀 TwoRun.AI by OAWO Inc. & Baku Business Group 2025
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
